import type { EntityDetailMetadata } from '@zag/ui';

const PLANS_UPDATE = { mode: 'all', permissions: ['plans:update'] } as const;
const SETTINGS_UPDATE = { mode: 'all', permissions: ['settings:update'] } as const;

/** Detail metadata for pages owned directly by the tenant application. */
export const APP_ENTITY_DETAIL_METADATA = [
  {
    entity: 'calendar-slot',
    route: '/calendars/:calendarId/slots/:slotId',
    titleKey: 'calendars.appointment.editTitle',
    sections: [
      { id: 'details', titleKey: 'calendars.settings.sections.details' },
      { id: 'recurrence', titleKey: 'calendars.appointment.recurrence.label', requiresEntityId: true },
      { id: 'custom-properties', titleKey: 'common.customProperties.sectionTitle', requiresEntityId: true },
    ],
    actions: [
      { id: 'save', labelKey: 'common.save', visibleWhen: { mode: 'all', permissions: ['calendars:update'] }, intent: 'mutation' },
      {
        id: 'delete',
        labelKey: 'common.delete',
        visibleWhen: { mode: 'all', permissions: ['calendars:update'] },
        requiresEntityId: true,
        intent: 'mutation',
      },
    ],
  },
  {
    entity: 'plan',
    route: '/planning/plans/:id',
    titleKey: 'plans.title',
    sections: [
      { id: 'details', titleKey: 'plans.details' },
      { id: 'plan-settings', titleKey: 'plans.planSettings', requiresEntityId: true },
      { id: 'shifts', titleKey: 'plans.shifts', requiresEntityId: true },
      { id: 'shift-units', titleKey: 'plans.drawerShiftUnits', requiresEntityId: true },
      { id: 'versions', titleKey: 'planning.availablePlanVersions', requiresEntityId: true },
    ],
    actions: [
      { id: 'save', labelKey: 'common.save', visibleWhen: PLANS_UPDATE, intent: 'mutation' },
      { id: 'delete', labelKey: 'common.delete', visibleWhen: PLANS_UPDATE, requiresEntityId: true, intent: 'mutation' },
      {
        id: 'resolve-conflicts',
        labelKey: 'planningOverrides.resolveConflicts',
        sectionId: 'shifts',
        visibleWhen: PLANS_UPDATE,
        requiresEntityId: true,
        intent: 'mutation',
      },
      {
        id: 'run-ai-solver',
        labelKey: 'plans.solver.aiSolver',
        sectionId: 'shifts',
        visibleWhen: PLANS_UPDATE,
        requiresEntityId: true,
        intent: 'mutation',
      },
      {
        id: 'open-bench',
        labelKey: 'plans.workspace.openBench',
        sectionId: 'shifts',
        visibleWhen: PLANS_UPDATE,
        requiresEntityId: true,
        intent: 'navigate',
      },
      {
        id: 'create-blank-version',
        labelKey: 'plans.versions.createBlank',
        sectionId: 'versions',
        visibleWhen: PLANS_UPDATE,
        requiresEntityId: true,
        intent: 'mutation',
      },
    ],
  },
  {
    entity: 'planning-period',
    route: '/planning/periods/:date/:teamId/:instanceId',
    titleKey: 'planningPeriods.viewTitle',
    sections: [
      { id: 'view', titleKey: 'planningPeriods.viewTitle' },
      { id: 'versions', titleKey: 'planning.availablePlanVersions', requiresEntityId: true },
    ],
    actions: [
      { id: 'create-period', labelKey: 'planningPeriods.createPeriod', visibleWhen: PLANS_UPDATE, intent: 'mutation' },
      {
        id: 'resolve-conflicts',
        labelKey: 'planningOverrides.resolveConflicts',
        visibleWhen: PLANS_UPDATE,
        requiresEntityId: true,
        intent: 'mutation',
      },
      { id: 'run-ai-solver', labelKey: 'plans.solver.aiSolver', visibleWhen: PLANS_UPDATE, requiresEntityId: true, intent: 'mutation' },
      {
        id: 'clear-assignments',
        labelKey: 'planning.commands.clear.action',
        visibleWhen: PLANS_UPDATE,
        requiresEntityId: true,
        intent: 'mutation',
      },
      {
        id: 'swap-assignments',
        labelKey: 'planning.commands.swap.action',
        visibleWhen: PLANS_UPDATE,
        requiresEntityId: true,
        intent: 'mutation',
      },
    ],
  },
  {
    entity: 'shift-unit',
    route: '/planning/plans/:id/shift-units/:unitId',
    titleKey: 'plans.capacity.defaultCapacity',
    sections: [{ id: 'capacity', titleKey: 'plans.capacity.defaultCapacity', requiresEntityId: true }],
    actions: [
      { id: 'save', labelKey: 'common.save', visibleWhen: PLANS_UPDATE, intent: 'mutation' },
      { id: 'cancel', labelKey: 'common.cancel', intent: 'navigate' },
    ],
  },
  {
    entity: 'custom-property-definition',
    route: '/settings/custom-properties/:id',
    titleKey: 'settings.customPropertyDefinitions.title',
    sections: [
      { id: 'definition', titleKey: 'settings.customPropertyDefinitions.sections.definition' },
      { id: 'section-details', titleKey: 'settings.customPropertyDefinitions.sections.sectionDetails' },
      { id: 'select-options', titleKey: 'settings.customPropertyDefinitions.sections.selectOptions' },
      { id: 'tag-options', titleKey: 'settings.customPropertyDefinitions.sections.tagOptions' },
      { id: 'number-config', titleKey: 'settings.customPropertyDefinitions.sections.numberConfig' },
      { id: 'text-config', titleKey: 'settings.customPropertyDefinitions.sections.textConfig' },
      { id: 'reference-config', titleKey: 'settings.customPropertyDefinitions.sections.referenceConfig' },
    ],
    actions: [
      { id: 'save', labelKey: 'common.save', visibleWhen: SETTINGS_UPDATE, intent: 'mutation' },
      { id: 'delete', labelKey: 'common.delete', visibleWhen: SETTINGS_UPDATE, requiresEntityId: true, intent: 'mutation' },
    ],
  },
  {
    entity: 'api-key',
    route: '/settings/api-keys/:id',
    titleKey: 'apiKeys.edit.title',
    sections: [
      { id: 'details', titleKey: 'apiKeys.edit.title' },
      { id: 'scope', titleKey: 'apiKeys.scope.title' },
      { id: 'created', titleKey: 'apiKeys.created.title', requiresEntityId: true },
    ],
    actions: [
      { id: 'create', labelKey: 'apiKeys.create.submit', intent: 'mutation' },
      { id: 'save', labelKey: 'common.save', requiresEntityId: true, intent: 'mutation' },
      { id: 'copy', labelKey: 'apiKeys.created.copy', sectionId: 'created', requiresEntityId: true, intent: 'mutation' },
      { id: 'done', labelKey: 'common.done', intent: 'navigate' },
      { id: 'back-to-list', labelKey: 'apiKeys.backToList', intent: 'navigate' },
    ],
  },
] satisfies readonly EntityDetailMetadata[];
